import axios from "axios";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Config from "../../../../Config";
import { color_black_ultra_light, color_grey_light, color_red_default, color_white } from "../../../../constants/colors";
import { MODAL_Z_INDEX_NESTED } from "../../../../constants/layers";
import { FONT_SIZE_SMALL } from "../../../../constants/fontSizes";
import { useI18n } from "../../../../i18n/I18nProvider";
import { getWeeklyTasksList } from "../../../../pages/personal/PersonalPage";
import { replaceAllTasks } from "../../../../store/tasksListSlice";
import { useWindowDimensions } from "../../../../utils";
import {
    bottomSheetDragHandleVisualStyle,
    useBottomSheetDrag,
} from "../../../../hooks/useBottomSheetDrag";
import { Button } from "../../../controls/Button/Button";
import { LoadingContainer } from "../../../containers/LoadingContainer";
import ModalQuestion from "../../../blocks/ModalQuestion";
import { ModalDefault } from "../../../containers/ModalDefault";
import { OnestBoldMed } from "../../../styled/TextComponents";
import { styles } from "./Styles";
import { WeeklyTaskModalRow } from "./WeeklyTaskModalRow";
import { Link } from "react-router-dom";

const cloneTasks = (tasks) => (tasks || []).map((task) => ({ ...task }));

const hasUnsavedChanges = (snapshot, draft) => {
    const snapshotById = new Map(snapshot.map((task) => [task.id, task]));
    const draftIds = new Set(draft.map((task) => task.id));

    if (snapshot.some((task) => !draftIds.has(task.id))) return true;

    return draft.some((task) => {
        const original = snapshotById.get(task.id);
        if (!original) return (task.title || "").trim() !== "";
        return (task.title || "") !== (original.title || "");
    });
};


export const WeeklyTasksUpdateModal = ({ isShown, onRequestClose }) => {
    const { t } = useI18n();
    const { height: screenHeight, width: screenWidth } = useWindowDimensions();

    const tasksList = useSelector(state => state.tasksList);
    const [draft, setDraft] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false);
    const snapshotRef = useRef([]);
    const dispatch = useDispatch();
    const canSave = useMemo(
        () => hasUnsavedChanges(snapshotRef.current, draft),
        [draft],
    );

    useLayoutEffect(() => {
        if (!isShown) return;
        const snapshot = cloneTasks(tasksList);
        snapshotRef.current = snapshot;
        setDraft(cloneTasks(snapshot));
        setIsSaving(false);
        setIsDiscardConfirmOpen(false);
    }, [isShown]);

    const scrollContainerRef = useRef(null);

    useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({
                top: scrollContainerRef.current.scrollHeight,
                behavior: "smooth"
            });
        }
    }, [draft.length]);

    function handleTaskRemove(taskId) {
        setDraft((current) => current.filter((task) => task.id !== taskId));
    }

    async function handleTasksChangeSubmit() {
        if (!canSave || isSaving) return;
        const snapshot = snapshotRef.current;
        const snapshotById = new Map(snapshot.map((task) => [task.id, task]));
        const draftIds = new Set(draft.map((task) => task.id));
        setIsSaving(true);
        try {
            for (const task of snapshot) {
                if (!draftIds.has(task.id)) {
                    await axios.delete(Config.BACKEND_ADDR + `/tasks/${task.id}`);
                }
            }
            for (const task of draft) {
                const original = snapshotById.get(task.id);
                if (!original) continue;
                if ((task.title || "") === (original.title || "")) continue;
                await axios.patch(Config.BACKEND_ADDR + `/tasks/`, {
                    task_id: task.id,
                    task_text: task.title,
                    completed: task.completed,
                });
            }
            for (const task of draft) {
                if (snapshotById.has(task.id)) continue;
                const title = (task.title || "").trim();
                if (!title) continue;
                await axios.post(Config.BACKEND_ADDR + `/tasks/`, { task_text: title });
            }
            dispatch(replaceAllTasks(await getWeeklyTasksList()));
            onRequestClose();
        } catch (error) {
            console.error("Failed to save weekly tasks:", error);
            setIsSaving(false);
        }
    }

    function handleExit() {
        if (isSaving) return;
        if (canSave) {
            setIsDiscardConfirmOpen(true);
            return;
        }
        onRequestClose();
    }

    function handleDiscardConfirm(confirmed) {
        setIsDiscardConfirmOpen(false);
        if (confirmed) onRequestClose();
    }

    const isPhoneSheet = screenWidth <= 460;
    const { dragHandleProps, sheetMotionStyle, resetDrag } = useBottomSheetDrag({
        enabled: isPhoneSheet,
        onClose: handleExit,
    });

    useEffect(() => {
        if (!isShown) resetDrag();
    }, [isShown, resetDrag]);


    return (
        <>
        <ModalQuestion
            type="question"
            isOpen={isDiscardConfirmOpen}
            h2={t("Отменить изменения?")}
            p={t("Есть несохранённые изменения. Если закрыть окно, они пропадут.")}
            yesText={t("Отменить")}
            noText={t("Остаться")}
            onClose={handleDiscardConfirm}
            zIndex={MODAL_Z_INDEX_NESTED}
        />
        <ModalDefault
            isOpen={isShown}
            onRequestClose={() => handleExit()}
            mobileBottomSheet={isPhoneSheet}
        >
            <div style={{
                ...styles.weeklyTasksModalContainer,
                ...sheetMotionStyle,
                width: isPhoneSheet ? "100%" : 430,
                maxWidth: "100%",
                marginTop: isPhoneSheet ? "auto" : undefined,
                height: isPhoneSheet
                    ? "auto"
                    : Math.min(640, Math.max(360, screenHeight - 80)),
                maxHeight: isPhoneSheet ? "78dvh" : "calc(100vh - 32px)",
                borderRadius: isPhoneSheet ? "20px 20px 0 0" : 20,
                paddingBottom: isPhoneSheet
                    ? "calc(16px + env(safe-area-inset-bottom))"
                    : 16,
                boxSizing: "border-box",
                alignItems: "stretch",
            }}>
                {isPhoneSheet ? (
                    <div
                        {...dragHandleProps}
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            padding: "4px 0 8px",
                            cursor: "grab",
                            touchAction: "none",
                        }}
                    >
                        <div style={bottomSheetDragHandleVisualStyle} />
                    </div>
                ) : null}
                <div
                    style={{
                        width: "100%",
                        marginBottom: 4,
                        marginTop: 5,
                    }}
                >
                    <OnestBoldMed>{t("Мои задачи")}</OnestBoldMed>
                </div>
                <LoadingContainer isLoading={isSaving}>
                    <div
                        ref={scrollContainerRef}
                        style={{
                            width: "100%",
                            flex: isPhoneSheet ? undefined : 1,
                            minHeight: 0,
                            maxHeight: isPhoneSheet ? "calc(78dvh - 150px)" : undefined,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "stretch",
                            overflowY: "auto",
                            scrollBehavior: "smooth",
                        }}
                    >
                        {draft.map((el) => {
                            return <WeeklyTaskModalRow
                                key={el.id}
                                task={el}
                                width="100%"
                                onTaskChange={(updatedTask) => {
                                    setDraft((current) => current.map((task) => (
                                        task.id === updatedTask.id ? { ...task, ...updatedTask } : task
                                    )))
                                }}
                                onTaskDeleteClick={(deletedTask) => handleTaskRemove(deletedTask.id)}
                            />
                        })}
                    </div>
                    {!draft.some(task => !task.title || task.title.trim() === "") && (
                        <div onClick={() => {
                            setDraft((current) => [
                                ...current,
                                {
                                    id: [...Array(15)].map(() => Math.floor(Math.random() * 16).toString(16)).join(''),
                                    title: "",
                                    newTask: true,
                                    completed: false,
                                },
                            ])
                        }}
                            style={{
                                width: "100%",
                                padding: "4px 0 8px",
                            }}
                        >
                            <Link
                                style={{
                                    color: color_red_default,
                                    textDecoration: "none",
                                    fontFamily: "Onest",
                                    fontSize: 14,
                                }}
                                color={color_red_default}
                            >
                                {t("+ Добавить задачу")}
                            </Link>
                        </div>
                    )}

                    <div
                        style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 12,
                            paddingTop: 8,
                        }}
                    >
                        <button
                            type="button"
                            onClick={handleExit}
                            style={{
                                border: "none",
                                background: "transparent",
                                color: color_black_ultra_light,
                                fontFamily: "Onest",
                                fontSize: 14,
                                cursor: "pointer",
                                padding: "8px 4px",
                            }}
                        >
                            {t("Отмена")}
                        </button>
                        <div
                            onClick={() => {
                                if (canSave && !isSaving) handleTasksChangeSubmit();
                            }}
                        >
                            <Button
                                outline={false}
                                width={140}
                                height={40}
                                backgroundColor={!canSave || isSaving ? color_grey_light : color_red_default}
                                disabled={!canSave || isSaving}
                                color={color_white}
                                borderColor={color_red_default}
                                style={{ borderWidth: 0 }}
                                fontSize={FONT_SIZE_SMALL}
                            >
                                {t("Сохранить")}
                            </Button>
                        </div>
                    </div>
                </LoadingContainer>
            </div>
        </ModalDefault>
        </>
    )
}